var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { memo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import { ButtonNew, ButtonNewVariant, } from 'root/src/client/v2/common/components/form/ButtonNew';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { MAX_DAILY_WINS, MAX_PARTICIPANTS_TO_START, MAX_REPEAT_TIMES, MAX_VOUCHER_COUNT_PER_WINNER, MAX_WINNERS_PER_GIVEAWAY, MIN_DAILY_WINS, MIN_PARTICIPANTS_TO_START, MIN_REPEAT_TIMES, MIN_VOUCHER_COUNT_PER_WINNER, MIN_WINNERS_PER_GIVEAWAY, WAIT_TIME_PRESETS, } from 'root/src/shared/giveaways/giveaways.constants';
import { useCreatorData } from 'root/src/client/logic/api/hooks/useCreatorData';
import { NumericStepper } from 'root/src/client/v2/common/components/form/NumericStepper';
export const AdvancedSettingsSection = () => {
    const { isOpen, onToggle, isMaxDailyWinsEnabled, isRepeatingEnabled, isMinParticipantsEnabled, isPartnerOrAffiliate, } = useAdvancedSettingsSection();
    return (<div className="w-[90%] gap-2 flex flex-col items-center">
			<ToggleButton isOpen={isOpen} onToggle={onToggle}/>
			{isOpen && (<AdvancedSettingsBox>
					{isPartnerOrAffiliate && <SubscribersPreferenceField />}
					<BlockBannedUsersField />
					<MaxWinnersField />
					<VoucherCountPerWinnerField />
					<EnableDailyWinsLimitField />
					{isMaxDailyWinsEnabled && <DailyWinsLimitField />}
					<EnableMinParticipantsField />
					{isMinParticipantsEnabled && <MinParticipantsToStartField />}
					{isMinParticipantsEnabled && <MaxWaitTimeField />}
					<EnableScheduleField />
					{isRepeatingEnabled && <RepeatTimesField />}
				</AdvancedSettingsBox>)}
		</div>);
};
export const AdvancedSettingsBox = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    return (<div className="border-[1px] bg-dark-2 border-light-5 w-full space-y-6 rounded-lg p-5" {...props}>{children}</div>);
};
const useAdvancedSettingsSection = () => {
    const creatorData = useCreatorData();
    // Only 'partner' and 'affiliate' twitch creators can have subscribers
    const isPartnerOrAffiliate = (creatorData === null || creatorData === void 0 ? void 0 : creatorData.broadcasterType) === 'partner'
        || (creatorData === null || creatorData === void 0 ? void 0 : creatorData.broadcasterType) === 'affiliate';
    const [isOpen, setOpen] = useState(false);
    const onToggle = () => {
        setOpen(prevState => !prevState);
    };
    const form = useFormContext();
    const isRepeatingEnabled = form.watch('isRepeatingEnabled');
    const isMaxDailyWinsEnabled = form.watch('isMaxDailyWinsEnabled');
    const isMinParticipantsEnabled = form.watch('isMinParticipantsEnabled');
    return {
        isOpen,
        onToggle,
        isRepeatingEnabled,
        isMaxDailyWinsEnabled,
        isMinParticipantsEnabled,
        isPartnerOrAffiliate,
    };
};
const ToggleButton = ({ isOpen, onToggle }) => {
    return (<ButtonNew type="button" variant={ButtonNewVariant.Outline} className="w-full border-[1px] bg-secondary !border-primary hover:border-secondary hover:bg-primary" onClick={onToggle}>
			{isOpen ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
		</ButtonNew>);
};
export const AdvancedSettingsField = ({ children, id, label, helperText, }) => {
    return (<div className="flex justify-between items-center">
			<label className="flex flex-col cursor-pointer select-none" htmlFor={id}>
				<p className="font-bold text-base">{label}</p>
				<p className="text-xs text-light-4">{helperText}</p>
			</label>
			<div>{children}</div>
		</div>);
};
const EnableScheduleField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="isRepeatingEnabled" render={({ field }) => {
            return (<AdvancedSettingsField id="enableRepeating" label="Enable Repeating" helperText="Automatically repeat this giveaway">
						<Checkbox id="enableRepeating" {...field} checked={field.value}/>
					</AdvancedSettingsField>);
        }}/>);
});
const SubscribersPreferenceField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="isTwitchSubscribersOnly" render={({ field }) => {
            return (<AdvancedSettingsField id="isSubscribersPreference" label="Subscriber Preference" helperText="Reward your Twitch Subscribers with bonus entries">
						<Checkbox id="isSubscribersPreference" {...field} checked={field.value}/>
					</AdvancedSettingsField>);
        }}/>);
});
const BlockBannedUsersField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="blockBannedUsers" render={({ field }) => {
            return (<AdvancedSettingsField id="blockBannedUsers" label="Block Banned Users" helperText="Prevent banned users from entering">
						<Checkbox id="blockBannedUsers" {...field} checked={field.value}/>
					</AdvancedSettingsField>);
        }}/>);
});
const MaxWinnersField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="maxWinners" render={({ field }) => {
            return (<AdvancedSettingsField id="maxWinners" label="Number of Winners" helperText="How many winners to select">
						<NumericStepper min={MIN_WINNERS_PER_GIVEAWAY} max={MAX_WINNERS_PER_GIVEAWAY} value={field.value} onChange={field.onChange}/>
					</AdvancedSettingsField>);
        }}/>);
});
const VoucherCountPerWinnerField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="voucherCountPerWinner" render={({ field }) => {
            return (<AdvancedSettingsField id="voucherCountPerWinner" label="Rewards Per Winner" helperText="How many rewards each winner receives">
						<NumericStepper min={MIN_VOUCHER_COUNT_PER_WINNER} max={MAX_VOUCHER_COUNT_PER_WINNER} value={field.value} onChange={field.onChange}/>
					</AdvancedSettingsField>);
        }}/>);
});
const EnableDailyWinsLimitField = () => {
    const form = useFormContext();
    return (<Controller control={form.control} name="isMaxDailyWinsEnabled" render={({ field }) => {
            return (<AdvancedSettingsField id="isMaxDailyWinsEnabled" label="Enable Daily Wins Limit" helperText="Max wins per user per day">
						<Checkbox id="isMaxDailyWinsEnabled" {...field} checked={field.value}/>
					</AdvancedSettingsField>);
        }}/>);
};
const DailyWinsLimitField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="maxDailyUserWins" render={({ field }) => {
            return (<AdvancedSettingsField id="maxDailyUserWins" label="Daily Wins Limit" helperText="How many times user can win per day?">
						<NumericStepper min={MIN_DAILY_WINS} max={MAX_DAILY_WINS} value={field.value || 1} onChange={field.onChange}/>
					</AdvancedSettingsField>);
        }}/>);
});
const EnableMinParticipantsField = () => {
    const form = useFormContext();
    return (<Controller control={form.control} name="isMinParticipantsEnabled" render={({ field }) => {
            return (<AdvancedSettingsField id="isMinParticipantsEnabled" label="Enable Min Participants" helperText="Cancel giveaway if not enough participants">
						<Checkbox id="isMinParticipantsEnabled" {...field} checked={field.value}/>
					</AdvancedSettingsField>);
        }}/>);
};
const MinParticipantsToStartField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="minParticipantsToStart" render={({ field }) => {
            const value = field.value || 1;
            const handleChange = (e) => {
                const val = e.target.value;
                if (val === '') {
                    field.onChange(MIN_PARTICIPANTS_TO_START);
                    return;
                }
                const num = parseInt(val, 10);
                if (!Number.isNaN(num)) {
                    const clamped = Math.min(MAX_PARTICIPANTS_TO_START, Math.max(MIN_PARTICIPANTS_TO_START, num));
                    field.onChange(clamped);
                }
            };
            const onMinus = () => {
                field.onChange(Math.max(MIN_PARTICIPANTS_TO_START, value - 1));
            };
            const onPlus = () => {
                field.onChange(Math.min(MAX_PARTICIPANTS_TO_START, value + 1));
            };
            return (<AdvancedSettingsField id="minParticipantsToStart" label="Min Participants" helperText="Minimum participants required to resolve">
						<div className="flex items-center gap-3">
							<button type="button" onClick={onMinus} className="p-1 bg-slate-700 rounded hover:bg-slate-600 disabled:brightness-75 disabled:hover:bg-slate-700" disabled={value <= MIN_PARTICIPANTS_TO_START}>
								<RemoveIcon fontSize="medium"/>
							</button>
							<input type="number" id="minParticipantsToStart" min={MIN_PARTICIPANTS_TO_START} max={MAX_PARTICIPANTS_TO_START} value={value} onChange={handleChange} className="w-16 text-center font-bold bg-slate-700 rounded px-2 py-1 border border-slate-600 focus:outline-none focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"/>
							<button type="button" onClick={onPlus} className="p-1 bg-slate-700 rounded hover:bg-slate-600 disabled:brightness-75 disabled:hover:bg-slate-700" disabled={value >= MAX_PARTICIPANTS_TO_START}>
								<AddIcon fontSize="medium"/>
							</button>
						</div>
					</AdvancedSettingsField>);
        }}/>);
});
const MaxWaitTimeField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="maxWaitTime" render={({ field }) => {
            const currentValue = field.value || WAIT_TIME_PRESETS[2].value;
            return (<AdvancedSettingsField id="maxWaitTime" label="Max Wait Time" helperText="How long to wait for min participants">
						<select id="maxWaitTime" value={currentValue} onChange={e => field.onChange(Number(e.target.value))} className="bg-slate-700 rounded px-3 py-1 border border-slate-600 focus:outline-none focus:border-primary cursor-pointer">
							{WAIT_TIME_PRESETS.map(preset => (<option key={preset.value} value={preset.value}>
									{preset.label}
								</option>))}
						</select>
					</AdvancedSettingsField>);
        }}/>);
});
const RepeatTimesField = memo(() => {
    const form = useFormContext();
    return (<Controller control={form.control} name="repeatTimes" render={({ field }) => {
            return (<AdvancedSettingsField id="repeatTimes" label="Repeat Times" helperText="How many times giveaway will repeat?">
						<NumericStepper min={MIN_REPEAT_TIMES} max={MAX_REPEAT_TIMES} value={field.value} onChange={field.onChange}/>
					</AdvancedSettingsField>);
        }}/>);
});
